/**
 * GLOB-4954: read the GA4 client id and session id from the first-party GA
 * cookies so they can be forwarded on bid / offer submission API calls.
 *
 * This util is intentionally defensive: it NEVER throws and is SSR-safe. Any
 * missing cookie, garbage value, or absent `document` yields `undefined`
 * fields, so callers degrade to today's payload (fields omitted entirely) when
 * cookies are absent, consent is declined, or GA is blocked.
 *
 * GLOB-5129: this module reads cookies but does NOT decide consent. Consent is
 * an input, supplied by the caller — see `getGaSessionIdPayload`. The consent
 * *source* is the Google Consent Mode state that `useGaSessionIdPayload`
 * observes through fe-core; nothing here knows or cares which CMP vendor
 * produced it.
 */

/**
 * Backend wire names for the forwarded GA identifiers.
 *
 * Both the auction (place-bid) and offering (create/replace offer) services
 * landed on the SAME top-level camelCase field names, so a single shared
 * constant pair covers every bid and offer payload. A rename is a one-line
 * change here; all call sites derive their payload keys from these.
 *
 * These must match the backend capture branches — `mg-GLOB-4949-capture-ga-ids`
 * (auction) and `mg-GLOB-4950-capture-ga-ids` (offering). Until both are live on
 * dev the services reject unknown body fields, so this FE change must NOT merge
 * before them (see GLOB-4954).
 */
export const GA_CLIENT_ID_FIELD = 'gaClientId'
export const GA_SESSION_ID_FIELD = 'gaSessionId'

interface GaSessionIds {
  gaClientId?: string
  gaSessionId?: string
}

/**
 * Ready-to-spread request-body fragment keyed by the backend wire names above.
 * The generated API client DTOs don't include these fields yet (client regen
 * happens after the GLOB-4949/GLOB-4950 backend merges), so call sites extend
 * the DTO type locally, e.g. `CreateOfferDto & GaSessionIdPayload`.
 */
export type GaSessionIdPayload = {
  [GA_CLIENT_ID_FIELD]?: string
  [GA_SESSION_ID_FIELD]?: string
}

const parseCookies = (): Record<string, string> => {
  const out: Record<string, string> = {}
  // SSR-safe: bail out when there is no document.
  if (typeof document === 'undefined') return out

  const raw = document.cookie
  if (!raw) return out

  for (const part of raw.split(';')) {
    const eq = part.indexOf('=')
    if (eq === -1) continue
    const name = part.slice(0, eq).trim()
    if (!name) continue
    out[name] = part.slice(eq + 1).trim()
  }
  return out
}

/**
 * `_ga` cookie: `GA1.1.<clientId>` where `<clientId>` is `XXXXXXXXX.YYYYYYYYY`
 * (everything after the second dot). Returns undefined for missing / malformed
 * values.
 */
const parseGaClientId = (value: string | undefined): string | undefined => {
  if (!value) return undefined
  const match = value.match(/^GA\d+\.\d+\.(.+)$/)
  return match?.[1] || undefined
}

/**
 * `_ga_<CONTAINER>` cookie holds the session id as the first value segment
 * after the version prefix, in one of two shapes:
 *   GS1: `GS1.1.<sessionId>.<...dot-separated...>`
 *   GS2: `GS2.1.s<sessionId>$<...$-separated...>`
 * Returns undefined for missing / malformed values.
 */
const parseGaSessionId = (value: string | undefined): string | undefined => {
  if (!value) return undefined
  const match = value.match(/^GS(\d+)\.\d+\.(.+)$/)
  const version = match?.[1]
  const rest = match?.[2]
  if (!version || !rest) return undefined

  if (version === '2') {
    // GS2: first `$`-delimited segment, with the leading `s` marker stripped.
    const segment = rest.split('$')[0] ?? ''
    return segment.replace(/^s/, '') || undefined
  }

  // GS1 (and any older dot-delimited variant): first `.`-delimited segment.
  return rest.split('.')[0] || undefined
}

/**
 * Deterministically pick a single `_ga_*` (session) cookie name.
 *
 * The `_ga_*` suffix is the GA4 measurement id minus its `G-` prefix. That
 * measurement id is configured inside GTM (not in this app's code or env), so
 * we can't match on it here. When multiple `_ga_*` cookies exist we therefore
 * fall back to the first alphabetically — deterministic, if arbitrary. See
 * GLOB-4954: refine this once a measurement id is discoverable client-side.
 */
const pickSessionCookieName = (
  cookies: Record<string, string>
): string | undefined =>
  Object.keys(cookies)
    .filter((name) => /^_ga_[A-Za-z0-9]+$/.test(name))
    .sort()[0]

export const getGaSessionIds = (): GaSessionIds => {
  try {
    const cookies = parseCookies()
    const gaClientId = parseGaClientId(cookies['_ga'])
    const sessionCookieName = pickSessionCookieName(cookies)
    const gaSessionId = sessionCookieName
      ? parseGaSessionId(cookies[sessionCookieName])
      : undefined

    return { gaClientId, gaSessionId }
  } catch {
    // Belt-and-suspenders: never let telemetry parsing break a submission.
    return {}
  }
}

/**
 * Request-body fragment to spread into a bid / offer payload.
 *
 * Both ids are emitted together or not at all: a lone id is useless downstream
 * because it cannot be resolved to a GA4 session, so sending one would only
 * produce unattributable rows.
 *
 * GLOB-5129: `analyticsConsentGranted` is a REQUIRED argument rather than
 * something this function works out for itself. Two reasons:
 *
 * 1. Cookie presence is not a consent signal. The previous version had no
 *    consent input at all and relied on the fact that declining analytics
 *    stops GA from ever writing `_ga` / `_ga_*`. That only covers a visitor
 *    who declines *first*: Google Consent Mode does not delete cookies when
 *    consent is withdrawn, so someone who opted in and later opted out keeps a
 *    working cookie jar and would have been captured indefinitely.
 * 2. Making it a required parameter means a new call site cannot silently
 *    forget the gate — omitting it is a type error, not a privacy incident.
 *
 * Pass the live Google Consent Mode `analytics_storage` value. In practice
 * every call site gets it from `useGaSessionIdPayload`, which is the single
 * decision point; this signature exists so that stays true.
 *
 * This is belt-and-suspenders, not load-bearing. The auction and offering
 * services deliberately never reject a bid / offer over these fields — they
 * silently discard an unusable pair server-side (GLOB-4949 / GLOB-4950) — so
 * telemetry can never block a submission. Emitting nothing here just keeps the
 * payload byte-identical to today's whenever consent is absent or withdrawn, a
 * cookie is missing, GA is blocked, or only one cookie parses.
 */
export const getGaSessionIdPayload = (
  analyticsConsentGranted: boolean
): GaSessionIdPayload => {
  // Consent first: never even read the cookies without a grant.
  if (!analyticsConsentGranted) return {}

  const { gaClientId, gaSessionId } = getGaSessionIds()
  if (!gaClientId || !gaSessionId) return {}

  return {
    [GA_CLIENT_ID_FIELD]: gaClientId,
    [GA_SESSION_ID_FIELD]: gaSessionId,
  }
}
